import { useEffect, useRef, useState } from 'react';
import { CameraError } from './CameraError';
import { useCameraControls } from '@/hooks/useCameraControls';
import { IconButton } from '@/components/ui/icon-button';
import { RefreshCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

type CameraPreviewProps = {
  className?: string;
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  detectedId: string | null;
};

export function CameraPreview({ className = '', canvasRef, detectedId }: CameraPreviewProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [error, setError] = useState<string | null>(null);
  const [instanceKey, setInstanceKey] = useState(0);

  const frameIdRef = useRef<number | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Controls
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const { setZoom, zoom } = useCameraControls(track);
  const pinchStartDistanceRef = useRef<number | null>(null);
  const lastZoomRef = useRef<number>(zoom);

  // Handle pinch to zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      lastZoomRef.current = zoom;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDistanceRef.current !== null) {
      e.preventDefault();

      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);
      const delta = currentDistance - pinchStartDistanceRef.current;

      const sensitivity = 0.005;
      const newZoom = lastZoomRef.current + delta * sensitivity;
      setZoom(newZoom);
    }
  };

  // Camera setup
  useEffect(() => {
    const enableCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });

        streamRef.current = stream;
        const videoTrack = stream.getVideoTracks()[0] ?? null;
        videoTrackRef.current = videoTrack;
        setTrack(videoTrack); // Trigger camera control hook

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();

          if (canvasRef?.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            const drawLoop = () => {
              if (!video.paused && !video.ended && ctx) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              }

              if (!detectedId) {
                frameIdRef.current = requestAnimationFrame(drawLoop);
              }
            };

            drawLoop();
          }
        }
      } catch (err) {
        const message =
          err instanceof DOMException
            ? err.name === 'NotAllowedError'
              ? t('camera.error.notAllowed')
              : err.name === 'NotFoundError'
                ? t('camera.error.notFound')
                : err.name === 'AbortError'
                  ? t('camera.error.aborted')
                  : t('camera.error.unknown', { name: err.name })
            : t('camera.error.generic');

        console.error('Camera access error:', err);
        setError(message);
      }
    };

    const timer = setTimeout(enableCamera, 300);

    const currentVideo = videoRef.current;

    return () => {
      clearTimeout(timer);
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (currentVideo) {
        currentVideo.srcObject = null;
      }

      setTrack(null);
    };
  }, [facingMode, instanceKey, detectedId, canvasRef, t]);

  useEffect(() => {
    if (detectedId && streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [detectedId]);

  const toggleFacingMode = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const handleRetry = () => {
    setError(null);
    setInstanceKey((k) => k + 1);
  };

  return (
    <div>
      <div
        key={instanceKey}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        className={cn(
          'relative w-full overflow-hidden bg-black touch-none',
          isMobile ? 'h-[60vh]' : 'aspect-video',
          className
        )}
      >
        {error ? (
          <CameraError message={error} onRetry={handleRetry} />
        ) : (
          <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
        )}

        <canvas
          ref={canvasRef}
          className="h-full w-full"
          style={{ zIndex: 10, pointerEvents: 'none' }}
        />

        {!error && (
          <IconButton
            type="button"
            icon={RefreshCcw}
            label={t('camera.switchCamera')}
            onClick={toggleFacingMode}
            size="icon"
            className="absolute bottom-3 right-3 size-12 bg-background/80 text-foreground shadow-lg backdrop-blur hover:bg-background [&_svg:not([class*='size-'])]:size-6"
          />
        )}
      </div>
    </div>
  );
}
